import React, { useEffect, useMemo, useState } from "react";
import { SelectOption } from "../../input/AsyncSearchInput/types";
import { AsyncSearchInput } from "../../input/AsyncSearchInput/AsyncSearchInput";
import { SEARCH_COMPANY_QUERY } from "../../../apis/companySearch/searchCompany";
import { SpogCompany } from "../../../legacy/types/SpogCompany";
import { useLazyQuery } from "@apollo/client";
import { COMPANY_SEARCH_SLIM_QUERY } from "../../../apis/companySearch/companyList";
import { CompanySearchResponse } from "../../../types/companySearch";
import { COMPANY_SEARCH_CLIENT } from "../../../utils/consts";
import { CONSTANTS, QUERY_ARGS } from "./consts";

export type CompanyOption = SelectOption;

interface TargetOrganizationsInputProps {
  onCompaniesSelect: (companies: CompanyOption[]) => void;
  selectedCompanyIds?: string[];
}

function companyToSelectOption(company: SpogCompany) {
  return { label: `${company.name} - ${company.url}`, value: company.hgId };
}

function convertCompaniesToSelectOptionsArray(
  data?: SpogCompany[]
): SelectOption[] {
  if (!data || data.length === 0) {
    return [];
  }
  return data.map(companyToSelectOption);
}

export const TargetOrganizationsInput: React.FC<
  TargetOrganizationsInputProps
> = ({ onCompaniesSelect, selectedCompanyIds = [] }) => {
  const [selectedCompanies, setSelectedCompanies] = useState<CompanyOption[]>(
    []
  );

  const [fetchCompaniesByIds, { loading }] =
    useLazyQuery<CompanySearchResponse>(COMPANY_SEARCH_SLIM_QUERY, {
      context: { clientName: COMPANY_SEARCH_CLIENT },
    });

  const handleOptionsSelect = (options: CompanyOption[]) => {
    setSelectedCompanies(options);
    onCompaniesSelect(options);
  };

  const needToFetchCompanyIds = useMemo(
    () =>
      selectedCompanyIds.length > 0 &&
      !selectedCompanyIds.every((id) =>
        selectedCompanies.some((company) => company.value === id)
      ),
    [selectedCompanyIds, selectedCompanies]
  );

  // Fetch companies by IDs when the selectedCompanyIds change
  useEffect(() => {
    if (selectedCompanyIds.length === 0) {
      setSelectedCompanies([]);
      return;
    }

    const fetchCompanies = async () => {
      const data = await fetchCompaniesByIds({
        variables: {
          filters: {
            hgIds: selectedCompanyIds,
          },
          per: selectedCompanyIds.length,
        },
      });
      if (data?.data?.search?.data) {
        const options = convertCompaniesToSelectOptionsArray(
          data.data.search.data
        );
        setSelectedCompanies(options);
      }
    };

    if (needToFetchCompanyIds) {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyIds, needToFetchCompanyIds]);

  return (
    <AsyncSearchInput<CompanyOption, SpogCompany[]>
      id="target-organizations"
      searchKey="query"
      query={SEARCH_COMPANY_QUERY}
      clientName={COMPANY_SEARCH_CLIENT}
      selectedOptions={selectedCompanies}
      onOptionsSelect={handleOptionsSelect}
      convertResponseToOptions={convertCompaniesToSelectOptionsArray}
      responseDataPath="searchCompaniesV2.data"
      placeholderText={CONSTANTS.DEFAULT_PLACEHOLDER}
      minFilterLength={CONSTANTS.MIN_FILTER_LENGTH}
      debounceDelay={CONSTANTS.DEBOUNCE_DELAY}
      minCharMessage={CONSTANTS.MIN_CHAR_MESSAGE}
      queryArgs={QUERY_ARGS}
      showGlobalLoadingIndicator={loading}
      maxVisibleChips={10}
    />
  );
};
